import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { businessQuery, datamodelItemsQuery, datamodelsQuery } from '@queries';
import { SearchIcon } from '@svgs';

import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

import { ContentDatabaseItem } from './ContentDatabaseItem/ContentDatabaseItem.jsx';

const matchDatamodelItem = (datamodelItem, query) => {
  // determine filter match for a particular ContentItem

  // explicit matching only
  const exactMatchFields = [datamodelItem.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const datamodelItemFields = [datamodelItem.name];
  // location name
  if (
    datamodelItemFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)
  ) {
    return true;
  }

  return false;
};

const filterDatamodelItems = (dataModelItems, query) => {
  // client side filtering of datamodel items
  const cleanedQuery = query.toLowerCase().trim();
  return dataModelItems.filter((dataModelItem) => {
    if (matchDatamodelItem(dataModelItem, cleanedQuery)) {
      return dataModelItem;
    }
  });
};

export const ContentDatabaseItems = () => {
  const { t, i18n } = useTranslation();
  const { businessID } = useParams();

  // state used based on the currently selected datamodel filter
  const [datamodelId, setDatamodelID] = useState(false);

  // get business
  const { data: businessData } = useQuery(businessQuery(businessID));

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // get datamodels
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      datamodel_type: 'datamodel_item',
      order_by: 'name',
    }),
  );

  const filterParams = {
    order_by: 'name',
    limit: 500,
    _fields: ['id', 'name', 'datamodel_id'].join(','),
  };
  if (datamodelId) {
    filterParams.datamodel_id = datamodelId;
  }

  // get datamodel items
  const {
    data: datamodelItemsData,
    isPending: datamodelItemsIsPending,
    isError,
  } = useQuery(datamodelItemsQuery(filterParams));

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const filteredDatamodelItems =
    searchTerm && datamodelItemsData
      ? filterDatamodelItems(datamodelItemsData.objects, searchTerm)
      : datamodelItemsData?.objects;

  // no datamodel item types
  const hasDatamodelItemTypes =
    !datamodelsIsPending && datamodelsData.objects?.length > 0;

  const onTypeChange = (e) => {
    // set datamodel_id to refetch results
    setDatamodelID(e.target.value);
  };

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <span>{t('Content database')}</span>
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Content database')}</h1>
              {hasDatamodelItemTypes && (
                <div className="sb-button-list-inline">
                  <Link
                    to={`/dashboard/projects/${businessID}/contentitems/new`}
                    className="sb-btn sb-btn-cancel"
                  >
                    Add new
                  </Link>
                </div>
              )}
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form className="sb-manage-options-select">
                    <select
                      onChange={onTypeChange}
                      disabled={datamodelsIsPending}
                    >
                      {datamodelsIsPending && <option>...</option>}
                      {datamodelsData && (
                        <>
                          <option value="">All types...</option>
                          {datamodelsData.objects.map((datamodel) => {
                            return (
                              <option key={datamodel.id} value={datamodel.id}>
                                {datamodel.name}
                              </option>
                            );
                          })}
                        </>
                      )}
                    </select>
                  </form>
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={datamodelItemsIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right">
                <div className="sb-flex">
                  <Link
                    to={`/dashboard/projects/${businessID}/datamodels`}
                    className="sb-btn sb-btn-cancel"
                  >
                    Manage content types
                  </Link>
                </div>
              </div>
            </div>
            {datamodelItemsIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!datamodelItemsIsPending &&
              !datamodelsIsPending &&
              !hasDatamodelItemTypes && (
                <WelcomeElement>
                  <h1>Project content database</h1>
                  <p>
                    Content database items are shared across the project for
                    reuse across website templates.
                  </p>
                  <Link
                    to={`/dashboard/projects/${businessID}/datamodels/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Start by adding a content type
                  </Link>
                </WelcomeElement>
              )}
            {!datamodelItemsIsPending &&
              !datamodelsIsPending &&
              hasDatamodelItemTypes &&
              datamodelItemsData.objects.length == 0 && (
                <WelcomeElement>
                  <h1>Project content database</h1>
                  <p>
                    Content database items are shared across the project for
                    reuse across website templates.
                  </p>
                  <Link
                    to={`/dashboard/projects/${businessID}/contentitems/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Start by adding a content item
                  </Link>
                </WelcomeElement>
              )}
            {!datamodelItemsIsPending && !isError && (
              <div className="sb-content-view-row">
                {filteredDatamodelItems.map((datamodelItem) => {
                  return (
                    <ContentDatabaseItem
                      key={datamodelItem.id}
                      businessID={businessID}
                      datamodelItem={datamodelItem}
                      datamodelsData={datamodelsData}
                    />
                  );
                })}
              </div>
            )}
            {!datamodelItemsIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredDatamodelItems.length} of ${datamodelItemsData.objects.length} content item` +
                  (datamodelItemsData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
